interface WelcomeGreetingProps {
  /** Display name rendered inside the highlighter band. */
  userName: string;
  /** Sub-line under the greeting — first-time vs. returning copy. */
  message: string;
  className?: string;
}

/**
 * The Home page's greeting: "Welcome, <name>" with the name struck through by
 * a marker band that draws itself left-to-right on first paint.
 *
 * Presentational on purpose — `HomePage` owns the auth and first-time-user
 * reads and passes strings in, which is what makes the greeting storyable and
 * keeps the animation reviewable in isolation.
 */
export function WelcomeGreeting({
  userName,
  message,
  className = '',
}: WelcomeGreetingProps) {
  return (
    <div className={`mx-auto w-full max-w-4xl ${className}`}>
      {/* `text-title` (24px) on a phone, the original 30px from `sm`: at 390
          the greeting is a header, not a hero, and every row it saves is a
          row the composer and topics get above the fold.

          ALWAYS ONE LINE, whatever the name - it is user data, and the
          fallback is the email prefix, which has no spaces at all. "Welcome,"
          never shrinks; the name takes the rest of the line and ends in an
          ellipsis only when it does not fit. Cut by WIDTH, not a character
          count: glyphs differ (15 W's are twice as wide as 15 i's) and so do
          screens - about 17 characters of a name fit at 390, 40+ on desktop -
          so a fixed count either overflows or cuts names that would fit.
          `title` keeps the full name a hover away. The old absolutely-
          positioned band clipped whatever overflowed it with no ellipsis
          ("Venkatasubramanian" rendered as "Venkatasubramania"). */}
      <h2 className='mb-1.5 flex min-w-0 items-baseline gap-2 whitespace-nowrap text-title font-bold text-foreground sm:mb-2 sm:text-3xl'>
        <span className='shrink-0'>Welcome,</span>
        {/* The band is the SAME fill as `::selection` in globals.css -
            `brand-500` at 28% in light, 50% in dark (see Foundation/Selection
            for why the alpha cannot be shared across themes), so a
            highlighted name and a drag-selected word are the same gesture.
            The ink stays `--foreground` in both themes. It is the span's own
            background, resting at full size; `animate-highlight-sweep` grows
            it from 0 on first paint. */}
        <span
          title={`${userName}!`}
          className='min-w-0 animate-highlight-sweep truncate rounded-lg bg-[length:100%_100%] bg-no-repeat px-2.5 py-0.5 [background-image:linear-gradient(hsl(var(--brand-500)/0.28),hsl(var(--brand-500)/0.28))] dark:[background-image:linear-gradient(hsl(var(--brand-500)/0.5),hsl(var(--brand-500)/0.5))]'
        >
          {userName}!
        </span>
      </h2>
      <p className='text-body text-muted-foreground sm:text-base'>{message}</p>
    </div>
  );
}
